import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment.prod';
import { AuditDto } from '@models/authorizations/audit.dto';
import { GroupDto } from '@models/authorizations/group.model';
import { ResponseDto } from '@models/generic/response.dto';
import { UserInfoDto } from '@models/generic/user-info.dto';
import { RegulationDto } from '@models/regulation/regulation.dto';
import {
  ChangePasswordDto,
  LoginDto,
  LoginResponseDto,
} from '@models/users/login.dto';
import { SignInDto } from '@models/users/sign-in.dto';
import { SubscriberDto } from '@models/users/subscriber.dto';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { isEmpty } from '../utils/is-empty.function';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private _token: string | null | undefined = '';

  public get token(): string | null | undefined {
    return this._token;
  }

  public set token(value: string | null | undefined) {
    this._token = value;
  }

  public loadTokenFromStorage(): string | null | undefined {
    return this.storage.token;
  }

  public updateTokenToStorage(value: string | null | undefined) {
    this.storage.token = value;
  }

  private _user: SubscriberDto | null | undefined;

  public get user(): SubscriberDto | null | undefined {
    return this._user;
  }
  public set user(value: SubscriberDto | null | undefined) {
    this._user = value;
  }

  public loadUserFromStorage(): SubscriberDto | null | undefined {
    return !isEmpty(this.storage.user)
      ? JSON.parse(this.storage.user)
      : undefined;
  }
  public updateUserToStorage(value: SubscriberDto | null | undefined) {
    this.storage.user = !isEmpty(value) ? JSON.stringify(value) : undefined;
  }

  private _group: GroupDto | null | undefined;

  public get group(): GroupDto | null | undefined {
    return this._group;
  }

  public set group(value: GroupDto | null | undefined) {
    this._group = value;
  }

  public loadGroupFromStorage(): GroupDto | null | undefined {
    return !isEmpty(this.storage.group)
      ? JSON.parse(this.storage.group)
      : undefined;
  }
  public updateGroupToStorage(value: GroupDto | null | undefined) {
    this.storage.group = !isEmpty(value) ? JSON.stringify(value) : undefined;
  }

  public get isLoggedIn(): boolean {
    return !isEmpty(this.token);
  }

  private mustChangePasswordSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  public get mustChangePassword(): Observable<boolean> {
    return this.mustChangePasswordSubject.asObservable();
  }

  private emptyRegulationsSubject: BehaviorSubject<RegulationDto[]> =
    new BehaviorSubject(<RegulationDto[]>[]);

  public get emptyRegulations(): Observable<RegulationDto[]> {
    return this.emptyRegulationsSubject.asObservable();
  }

  private UserPath = {
    loginPath: () => `/auth/login`,
    logoutPath: () => `/auth/logout`,
    changePasswordPath: () => `/auth/change-password`,
    getUserInfoPath: () => `/auth/user-info`,
    getUserAuthPath: () => `/auth/user-authorization`,
    getAuthList: () => `/auth/list`,
    modifyAuthList: () => `/auth/modify`,
    createAuth: () => `/auth/create`,
    removeAuth: () => `/auth/remove`,
    userHistory: (userId: number, pageSize: number) =>
      `/auth/${userId}/history?pageSize=${pageSize}`,
    resetPassword: (userId: number) => `/auth/reset-password/${userId}`,
  };

  constructor(
    private http: HttpClient,
    private storage: StorageService,
  ) {
    this.group = this.loadGroupFromStorage();
    this.user = this.loadUserFromStorage();
    this.token = this.loadTokenFromStorage();
  }

  /**
   * API MANAGEMENT
   */

  login(loginDto: LoginDto) {
    return this.http
      .post<LoginResponseDto>(
        `${environment.host}${this.UserPath.loginPath()}`,
        loginDto,
      )
      .pipe(
        tap((response: LoginResponseDto) => {
          this.token = response.access_token;
          this.group = response.authorization;
          this.updateGroupToStorage(this.group);
          this.updateTokenToStorage(this.token);
        }),
      );
  }

  getUserInfo() {
    return this.http
      .get<UserInfoDto>(environment.host + this.UserPath.getUserInfoPath())
      .pipe(
        tap((response: UserInfoDto) => {
          this.user = response.subscriber;
          this.updateUserToStorage(this.user);
          this.mustChangePasswordSubject.next(
            response.mustChangePassword || false,
          );
          this.getUserAuthorization().subscribe();
        }),
      );
  }

  getUserAuthorization() {
    return this.http
      .get<LoginResponseDto>(environment.host + this.UserPath.getUserAuthPath())
      .pipe(
        tap((response: LoginResponseDto) => {
          this.group = response.authorization;
          this.updateGroupToStorage(this.group);
        }),
      );
  }

  changePassword(change: ChangePasswordDto) {
    return this.http.post<ChangePasswordDto>(
      environment.host + this.UserPath.changePasswordPath(),
      change,
    );
  }

  getAuthList() {
    return this.http.get<SignInDto[]>(
      environment.host + this.UserPath.getAuthList(),
    );
  }

  getUserHistory(userId: number, pageSize: number) {
    return this.http.get<AuditDto[]>(
      environment.host + this.UserPath.userHistory(userId, pageSize),
    );
  }

  resetPassword(userId: number) {
    return this.http.get<any>(
      environment.host + this.UserPath.resetPassword(userId),
    );
  }

  modifySignIn(signIn: any[]) {
    return this.http.post<any>(
      environment.host + this.UserPath.modifyAuthList(),
      signIn,
    );
  }

  createSignIn(signIn: any[]) {
    return this.http.post<any>(
      environment.host + this.UserPath.createAuth(),
      signIn,
    );
  }

  removeSignIn(signIn: any[]) {
    return this.http.post<any>(
      environment.host + this.UserPath.removeAuth(),
      signIn,
    );
  }

  logout() {
    return this.http
      .get<ResponseDto<any>>(environment.host + this.UserPath.logoutPath())
      .pipe(
        finalize(() => {
          this.clearUserData();
        }),
      );
  }

  clearUserData() {
    this.user = undefined;
    this.updateUserToStorage(this.user);
    this.token = undefined;
    this.updateTokenToStorage(this.token);
    this.group = undefined;
    this.updateGroupToStorage(this.group);
  }

  /**
   * SETTINGS MANAGEMENT
   */
}
